/*
    Timer component for slate void node and/or test page widget bar.
    Must be used within PollingContext
    Reads currentItem.timeLimit for countdown initial value
    Timestamps owned by this component are ms, timestamps in data are s

    data:
    {
        response_duration: seconds
        timer_reset: true // TODO
        timer_start: ms
        timer_stop: ms
    }
*/

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../../style/item-content/timer.module.css';
import { useCurrentItem, useData, useDataActions } from './polling-context';

const Timer = () => {
  const currentItem = useCurrentItem();
  const data = useData();
  const { updateData } = useDataActions();

  const [displayValue, setDisplayValue] = useState(null); // ms

  const isRunning = useMemo(() => data.timer_start && !data.timer_stop, [data]);
  const increment = useMemo(() => (currentItem.timer_countdown ? -1 : 1), [currentItem]); // the direction of the timer  1 || -1
  const response_duration = useMemo(() => data.response_duration, [data]);
  const timeLimit = useMemo(() => currentItem?.timeLimit, [currentItem]);
  const timer_start = useMemo(() => data.timer_start, [data]);
  const timer_stop = useMemo(() => data.timer_stop, [data]);

  const reset = useCallback(() => {
    setDisplayValue(() => (currentItem?.timeLimit != null ? currentItem.timeLimit * 1000 : 0));
    updateData({
      timer_reset: null,
      timer_start: null,
      timer_stop: null,
      timer_expired: null,
      response_duration: null
    });
  }, [currentItem, updateData]);

  // calculates and records result of start/stop timestamps
  const saveResult = useCallback(() => {
    if (!data.timer_start || !data.timer_stop) return;

    // caculate elapsed time and add to total. convert ms to s
    const currentDuration = data.response_duration * 1000 || (currentItem?.timeLimit ?? 0) * 1000;
    const elapsed = Math.max(0, data.timer_stop - data.timer_start) * increment;
    let newDuration = Math.max(0, currentDuration + elapsed);

    updateData({
      response_duration: newDuration / 1000,
      timer_start: null,
      timer_stop: null
    });
  }, [currentItem, data, increment, updateData]);

  const start = useCallback(() => {
    // don't override existing start time
    if (timer_start) return;

    // don't start if countdown is already at 0
    if (increment < 0 && typeof response_duration === 'number' && response_duration <= 0) return;

    updateData({ timer_start: Date.now() });
  }, [increment, response_duration, timer_start, updateData]);

  const stop = useCallback(
    ({ expired = false } = {}) => {
      // don't override existing stop time
      if (timer_stop) return;

      updateData({
        timer_stop: Date.now(),
        ...(expired ? { timer_expired: true } : {})
      });
    },
    [timer_stop, updateData]
  );

  const tick = useCallback(() => {
    setDisplayValue(() => {
      const baseDuration =
        response_duration != null ? response_duration * 1000 : (timeLimit ?? 0) * 1000;
      const elapsed = Date.now() - timer_start;
      return Math.max(0, baseDuration + elapsed * increment);
    });
  }, [increment, response_duration, timeLimit, timer_start]);

  // update display value when switching items
  // Key on currentItem?._id so a new item always starts from its own timeLimit,
  // ignoring any leftover response_duration from the previous item (which lingers
  // briefly in shared polling data until the server returns fresh data).
  useEffect(() => {
    setDisplayValue(() => {
      if (timeLimit != null) return timeLimit * 1000;
      else if (response_duration != null) return response_duration * 1000;
      else return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem?._id]);

  // if response_duration updates for the current item (e.g. after saveResult), reflect it.
  useEffect(() => {
    if (response_duration == null) return;
    setDisplayValue(response_duration * 1000);
  }, [response_duration]);

  // tick
  useEffect(() => {
    if (!isRunning) return;

    // start the interval
    const intervalID = setInterval(() => tick(), 125);

    // clean up interval
    return () => clearInterval(intervalID);
  }, [isRunning, tick]);

  // stop timer when countdown reaches 0
  useEffect(() => {
    // bandaid: in the case of a auto starting countdown, the display value is not updated quick enough. A race condition occurs that stops the timer in ~0.1 sec.
    // the last condition ads a 0.5 sec buffer where the timer will not be automatically stopped. this is not ideal, and sholud be considered temporary; though a proper fix would probably require a significant refactor...
    if (isRunning && increment < 0 && displayValue === 0 && Date.now() - timer_start > 500)
      stop({ expired: true });
  }, [displayValue, increment, isRunning, stop, timer_start]);

  // auto-start countdown for items flagged by API (e.g. ANCR timed items)
  useEffect(() => {
    if (
      currentItem?.timer_autoStart === true &&
      increment < 0 &&
      !timer_start &&
      !timer_stop &&
      (response_duration == null || response_duration > 0)
    ) {
      start();
    }
  }, [currentItem, increment, timer_start, timer_stop, response_duration, start]);

  // stop timer when moving away from current item
  useEffect(() => {
    if (
      isRunning &&
      (data.sessionControl_nextItem ||
        data.sessionControl_prevItem ||
        data.sessionControl_nextTest ||
        data.sessionControl_endTest)
    )
      stop();
  }, [data, isRunning, stop]);

  // save timer result when both timestamps are present
  useEffect(() => {
    if (timer_start != null && timer_stop != null) saveResult();
  }, [saveResult, stop, timer_start, timer_stop]);

  return (
    <div className={styles['container']}>
      <div className={styles['time-container']}>
        <label className={styles['time-label']}>{Math.round(displayValue / 1000)}</label>
      </div>
      {!currentItem?.timer_autoStart && (
        <div className={styles['button-container']}>
          <button className={styles['reset-button']} onClick={() => reset()}>
            reset
          </button>
          <button
            className={styles['start-stop-button'] + ' ' + (isRunning ? styles['running'] : null)}
            onClick={() => (isRunning ? stop() : start())}
          >
            {isRunning ? 'stop' : 'start'}
          </button>
        </div>
      )}
    </div>
  );
};

export default Timer;
